import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ConfigProvider, Drawer, Input, Popover, Select, Spin } from 'antd';
import { CheckOutlined, CloseOutlined, DownOutlined, DownloadOutlined, FilterOutlined, HistoryOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useAxiosClient from '../axios-client.js';
import { useStateContext } from '../contexts/ContextProvider.jsx';
import { useFabricTypes } from '../hooks/useFabricTypes.jsx';
import { useStockLocations } from '../hooks/useStockLocations.jsx';
import { useProductTypes } from '../hooks/useProductTypes.jsx';
import { useProductFormations } from '../hooks/useProductFormations.jsx';
import { useFabrics } from '../hooks/useFabrics.jsx';
import { useFabricPatterns } from '../hooks/useFabricPatterns.jsx';

// Design tokens ported from the "Ready Stock Redesign" mock — the same shell
// palette DefaultLayout publishes, re-declared here so a nested ConfigProvider's
// portalled popovers/drawers can read the same hex values.
import { PRIMARY, hexToRgba, SHELL_THEME as THEME, shellAntdTheme, STATUS } from '../shellTokens';

const PAGE_SIZE_OPTIONS = [15, 30, 50];
const REASON_PRESETS = ['Factory batch', 'Stock-take', 'Damaged'];

/**
 * Swatch hues for the seeded fabric colours (FabricColorSeeder). The colour
 * table stores names only — no hex column — so the dot is a rendering of the
 * name, not extra data. Anything unrecognised gets a neutral chip.
 */
const COLOR_HEX = {
  'maroon': '#6f2f3e', 'deep maroon': '#571f2c', 'violet': '#7c5bb5', 'coffee': '#5a4030',
  'milk coffee': '#8a6b52', 'ash': '#8d949e', 'brown': '#6b4a31', 'deep brown': '#4a3120',
  'green': '#3d6b52', 'sea green': '#2e6b62', 'golden': '#8a7433', 'navy blue': '#243a63',
  'royal blue': '#2b4a91', 'sky blue': '#4a86b8', 'cream': '#d8cdb4', 'lemon': '#c9c356',
  'olive': '#6b6b3a', 'gray': '#6c727c', 'orange': '#b5713a', 'pink': '#b56b85',
  'purple': '#6b4a8a', 'silver': '#a8adb6', 'red': '#9e3a3a', 'white': '#e8e2d5',
  'black': '#2a2d33', 'paste': '#5fa3a3',
};

/** Ledger vocabulary — label, tint and sign, in one place. */
const MOVEMENT_META = {
  RETURN_IN: { label: 'RETURN IN', tint: STATUS.ok },
  CANCEL_IN: { label: 'CANCEL IN', tint: STATUS.ok },
  MANUAL_IN: { label: 'MANUAL IN', tint: 'var(--activeText)', bg: 'var(--activeBg)' },
  ADJUSTMENT: { label: 'ADJUSTMENT', tint: STATUS.warn },
  PULL_OUT: { label: 'PULL OUT', tint: STATUS.errorSoft },
  PULL_UNDO: { label: 'PULL UNDO', tint: STATUS.ok },
};

const movementMeta = (type) => MOVEMENT_META[type] || { label: type || '—', tint: 'var(--muted)' };

const initials = (name) => (name || '')
  .split(/\s+/).filter(Boolean).slice(0, 2)
  .map((w) => w.charAt(0).toUpperCase()).join('') || '?';

const fmtAgo = (days) => {
  if (days === null || days === undefined) return null;
  return days === 0 ? 'today' : `${days}d`;
};

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

/** window width < 768, matching the mock's card-list breakpoint (1c). */
const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
};

const GRID = 'minmax(0,2.1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1.35fr) minmax(0,1.1fr) 110px 210px';

function ReadyStock() {
  const axiosClient = useAxiosClient();
  const { permissions, darkMode } = useStateContext();
  const isMobile = useIsMobile();

  const canManage = (permissions || []).includes('MANAGE_STOCK');

  const { fabricTypes } = useFabricTypes();
  const { stockLocations, fetchStockLocations } = useStockLocations();
  const { productTypes } = useProductTypes();
  const { productFormations, formationsByType } = useProductFormations({ includeLegacy: true });
  const { fabrics, fabricsLoading, hasMore, loadMore } = useFabrics({ includeStoreOnly: false });
  const { patternsFor, loadPatterns } = useFabricPatterns();

  const [kinds, setKinds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [matchedUnits, setMatchedUnits] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortField, setSortField] = useState('onHand');
  const [sortOrder, setSortOrder] = useState('desc');
  const [stats, setStats] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [productTypeId, setProductTypeId] = useState(null);
  const [productFormationId, setProductFormationId] = useState(null);
  const [fabricTypeId, setFabricTypeId] = useState(null);
  const [fabricColorId, setFabricColorId] = useState(null);
  const [fabricPatternId, setFabricPatternId] = useState(null);
  const [locationId, setLocationId] = useState(null); // id | 'none' | null
  const [inStockOnly, setInStockOnly] = useState(true);
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [sittingOnly, setSittingOnly] = useState(false);
  const [groupByType, setGroupByType] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [openFilter, setOpenFilter] = useState(null); // 'type' | 'formation' | 'fabric' | 'pattern' | 'color' | 'location' | 'rows' | null

  // Per-kind history drawer
  const [historyKind, setHistoryKind] = useState(null);
  const [movements, setMovements] = useState([]);
  const [historySummary, setHistorySummary] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Company-wide ledger drawer
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // Quick row add / adjust sheet
  const [entryKind, setEntryKind] = useState(null);
  const [entryMode, setEntryMode] = useState('add'); // 'add' | 'adjust'
  const [entryQty, setEntryQty] = useState(1);
  const [entryReason, setEntryReason] = useState('');
  const [saving, setSaving] = useState(false);

  // New stock addition sheet (header button / full catalogue)
  const [newStockOpen, setNewStockOpen] = useState(false);
  const [newProductType, setNewProductType] = useState(null);
  const [newFormation, setNewFormation] = useState(null);
  const [newFabricType, setNewFabricType] = useState(null);
  const [newColor, setNewColor] = useState(null);
  const [newPattern, setNewPattern] = useState(null);
  const [newQty, setNewQty] = useState(1);
  const [newReason, setNewReason] = useState('Opening stock');
  const [newLocation, setNewLocation] = useState(null);
  const [newBag, setNewBag] = useState('');
  const [detectedKind, setDetectedKind] = useState(null);
  const [detecting, setDetecting] = useState(false);

  // Placement sheet
  const [placementKind, setPlacementKind] = useState(null);
  const [placementLocation, setPlacementLocation] = useState(null);
  const [placementBag, setPlacementBag] = useState('');

  const searchBoxRef = useRef(null);

  const fetchStock = useCallback(async (overrides = {}) => {
    const p = overrides.page ?? page;
    const search = 'search' in overrides ? overrides.search : searchText;
    const productType = 'productTypeId' in overrides ? overrides.productTypeId : productTypeId;
    const formation = 'productFormationId' in overrides ? overrides.productFormationId : productFormationId;
    const fabric = 'fabricTypeId' in overrides ? overrides.fabricTypeId : fabricTypeId;
    const color = 'fabricColorId' in overrides ? overrides.fabricColorId : fabricColorId;
    const pattern = 'fabricPatternId' in overrides ? overrides.fabricPatternId : fabricPatternId;
    const location = 'locationId' in overrides ? overrides.locationId : locationId;
    const stocked = 'inStockOnly' in overrides ? overrides.inStockOnly : inStockOnly;
    const lowStock = 'lowStockOnly' in overrides ? overrides.lowStockOnly : lowStockOnly;
    const sitting = 'sittingOnly' in overrides ? overrides.sittingOnly : sittingOnly;
    const grouped = 'groupByType' in overrides ? overrides.groupByType : groupByType;
    const size = overrides.pageSize ?? pageSize;
    const field = overrides.sortField ?? sortField;
    const order = overrides.sortOrder ?? sortOrder;

    setLoading(true);
    try {
      const res = await axiosClient.get('/stock/index', {
        params: {
          page: p,
          perPage: size,
          sortField: field,
          sortOrder: order,
          inStockOnly: stocked,
          ...(lowStock ? { lowStockOnly: true } : {}),
          ...(sitting ? { sittingOnly: true } : {}),
          ...(grouped ? { groupByType: true } : {}),
          ...(search ? { search } : {}),
          ...(productType ? { productTypeId: productType } : {}),
          ...(formation ? { productFormationId: formation } : {}),
          ...(fabric ? { fabricTypeId: fabric } : {}),
          ...(color ? { fabricColorId: color } : {}),
          ...(pattern ? { fabricPatternId: pattern } : {}),
          ...(location ? { locationId: location } : {}),
        },
      });
      setKinds(res.data.data || []);
      setTotal(res.data.meta?.total ?? 0);
      setMatchedUnits(res.data.summary?.units ?? 0);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, [
    axiosClient,
    page,
    searchText,
    productTypeId,
    productFormationId,
    fabricTypeId,
    fabricColorId,
    fabricPatternId,
    locationId,
    inStockOnly,
    lowStockOnly,
    sittingOnly,
    groupByType,
    pageSize,
    sortField,
    sortOrder,
  ]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axiosClient.get('/stock/stats');
      setStats(res.data);
    } catch {
      // Tiles are supplementary — the list still works without them.
    }
  }, [axiosClient]);

  useEffect(() => { fetchStock(); }, [fetchStock]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // "/" focuses search, matching the affordance the mock draws on the search box.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        searchBoxRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const applyFilter = (patch) => {
    if ('search' in patch) setSearchText(patch.search);
    if ('productTypeId' in patch) setProductTypeId(patch.productTypeId);
    if ('productFormationId' in patch) setProductFormationId(patch.productFormationId);
    if ('fabricTypeId' in patch) {
      setFabricTypeId(patch.fabricTypeId);
      if (!patch.fabricTypeId && !('fabricPatternId' in patch)) {
        setFabricPatternId(null);
        patch.fabricPatternId = null;
      }
    }
    if ('fabricColorId' in patch) setFabricColorId(patch.fabricColorId);
    if ('fabricPatternId' in patch) setFabricPatternId(patch.fabricPatternId);
    if ('locationId' in patch) setLocationId(patch.locationId);
    if ('inStockOnly' in patch) setInStockOnly(patch.inStockOnly);
    if ('lowStockOnly' in patch) setLowStockOnly(patch.lowStockOnly);
    if ('sittingOnly' in patch) setSittingOnly(patch.sittingOnly);
    if ('pageSize' in patch) setPageSize(patch.pageSize);
    setOpenFilter(null);
    setPage(1);
    fetchStock({ ...patch, page: 1 });
  };

  const clearAllFilters = () => applyFilter({
    search: '',
    productTypeId: null,
    productFormationId: null,
    fabricTypeId: null,
    fabricColorId: null,
    fabricPatternId: null,
    locationId: null,
    lowStockOnly: false,
    sittingOnly: false,
  });

  const toggleSort = (field) => {
    const nextOrder = sortField === field && sortOrder === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortOrder(nextOrder);
    fetchStock({ sortField: field, sortOrder: nextOrder });
  };

  const goToPage = (p) => {
    setPage(p);
    fetchStock({ page: p });
  };

  const refreshAfterMutation = () => {
    fetchStock();
    fetchStats();
  };

  // -- History --
  const openHistory = async (kind) => {
    setHistoryKind(kind);
    setMovements([]);
    setHistorySummary(null);
    setHistoryLoading(true);
    try {
      const res = await axiosClient.get(`/stock/movements/${kind.id}`);
      setMovements(res.data.data || []);
      setHistorySummary(res.data.summary || null);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openLedger = async () => {
    setLedgerOpen(true);
    setLedger([]);
    setLedgerLoading(true);
    try {
      const res = await axiosClient.get('/stock/movements', { params: { perPage: 50 } });
      setLedger(res.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setLedgerLoading(false);
    }
  };

  // -- Add / adjust --
  const openEntry = (kind, mode) => {
    setEntryKind(kind);
    setEntryMode(mode);
    setEntryQty(mode === 'adjust' ? -1 : 1);
    setEntryReason('');
  };

  const submitEntry = async () => {
    if (!entryQty || Number.isNaN(entryQty)) {
      toast.error('Enter a quantity');
      return;
    }
    setSaving(true);
    try {
      await axiosClient.post('/stock/movements', {
        productKindId: entryKind.id,
        quantity: entryMode === 'adjust' ? entryQty : Math.abs(entryQty),
        type: entryMode === 'adjust' ? 'ADJUSTMENT' : 'MANUAL_IN',
        reason: entryReason || null,
      });
      toast.success('Stock updated');
      setEntryKind(null);
      refreshAfterMutation();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setSaving(false);
    }
  };

  // Load remaining colors if available
  useEffect(() => {
    if (hasMore && !fabricsLoading) {
      loadMore();
    }
  }, [hasMore, fabricsLoading, loadMore]);

  // Load patterns whenever selected fabric changes
  useEffect(() => {
    if (newFabricType) {
      loadPatterns(Number(newFabricType));
    }
  }, [newFabricType, loadPatterns]);

  const availablePatterns = useMemo(
    () => (newFabricType ? patternsFor(Number(newFabricType)) : []),
    [newFabricType, patternsFor],
  );

  // Load patterns for filter whenever fabricTypeId changes
  useEffect(() => {
    if (fabricTypeId) {
      loadPatterns(Number(fabricTypeId));
    }
  }, [fabricTypeId, loadPatterns]);

  const filterPatterns = useMemo(
    () => (fabricTypeId ? patternsFor(Number(fabricTypeId)) : []),
    [fabricTypeId, patternsFor],
  );

  const handleFabricChange = (val) => {
    setNewFabricType(val);
    setNewPattern(null);
  };

  // Real-time lookup for existing product kind matching the 5-tuple
  useEffect(() => {
    if (!newStockOpen || !newProductType || !newFormation || !newFabricType || !newColor) {
      setDetectedKind(null);
      setDetecting(false);
      return;
    }

    let active = true;
    setDetecting(true);

    axiosClient.get('/stock/find-kind', {
      params: {
        productTypeId: newProductType,
        productFormationId: newFormation,
        fabricTypeId: newFabricType,
        fabricColorId: newColor,
        fabricPatternId: newPattern || null,
      },
    })
      .then((res) => {
        if (!active) return;
        if (res.data?.found && res.data?.kind) {
          setDetectedKind(res.data.kind);
          if (res.data.kind.locationId) {
            setNewLocation((loc) => loc || res.data.kind.locationId);
          }
          const detectedBag = res.data.kind.bag || res.data.kind.bin;
          if (detectedBag) {
            setNewBag((b) => b || detectedBag);
          }
        } else {
          setDetectedKind(null);
        }
      })
      .catch(() => {
        if (active) setDetectedKind(null);
      })
      .finally(() => {
        if (active) setDetecting(false);
      });

    return () => { active = false; };
  }, [newStockOpen, newProductType, newFormation, newFabricType, newColor, newPattern, axiosClient]);

  const openNewStockModal = () => {
    setNewProductType(null);
    setNewFormation(null);
    setNewFabricType(null);
    setNewColor(null);
    setNewPattern(null);
    setNewQty(1);
    setNewReason('');
    setNewLocation(null);
    setNewBag('');
    setDetectedKind(null);
    setNewStockOpen(true);
  };

  const submitNewStock = async () => {
    if (!newProductType || !newFormation || !newFabricType || !newColor) {
      toast.error('Please select Product Type, Formation, Fabric, and Color');
      return;
    }
    const qty = parseInt(newQty, 10);
    if (!qty || qty < 1) {
      toast.error('Please enter a valid positive quantity');
      return;
    }

    setSaving(true);
    try {
      const bagValue = newBag ? newBag.trim() : null;
      const payload = {
        productTypeId: newProductType,
        productFormationId: newFormation,
        fabricTypeId: newFabricType,
        fabricColorId: newColor,
        fabricPatternId: newPattern || null,
        quantity: qty,
        type: 'MANUAL_IN',
        reason: newReason || 'Opening stock',
        stockLocationId: newLocation || null,
        bag: bagValue,
        bin: bagValue,
      };

      const res = await axiosClient.post('/stock/movements', payload);
      toast.success(res.data?.message || 'Stock added');
      setNewStockOpen(false);
      refreshAfterMutation();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setSaving(false);
    }
  };

  // -- Placement --
  const openPlacement = (kind) => {
    setPlacementKind(kind);
    setPlacementLocation(kind.locationId ?? null);
    setPlacementBag(kind.bag || kind.bin || '');
  };

  const submitPlacement = async () => {
    setSaving(true);
    try {
      const bagVal = placementBag ? placementBag.trim() : null;
      await axiosClient.put(`/stock/placement/${placementKind.id}`, {
        stockLocationId: placementLocation,
        bag: bagVal,
        bin: bagVal,
      });
      toast.success('Placement updated');
      setPlacementKind(null);
      fetchStock();
      fetchStockLocations();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInlinePlacementSave = async (kindId, newLocId, newBagVal) => {
    try {
      const bagVal = newBagVal ? newBagVal.trim() : null;
      await axiosClient.put(`/stock/placement/${kindId}`, {
        stockLocationId: newLocId,
        bag: bagVal,
        bin: bagVal,
      });
      toast.success('Placement updated');
      fetchStock();
      fetchStats();
      fetchStockLocations();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
      throw error;
    }
  };

  // Current page only — an honest label for what's actually exported, since
  // there's no backend endpoint for a full-catalogue export.
  const handleExport = () => {
    const header = ['Product type', 'Formation', 'Fabric', 'Color', 'Pattern', 'Location', 'Bag', 'Last movement', 'Days ago', 'On hand'];
    const rows = kinds.map((k) => [
      k.productType, k.productFormation, k.fabricType, k.fabricColor, k.fabricPattern || '',
      k.location || '', k.bag || k.bin || '',
      k.lastMovementType || '', k.daysSinceMovement ?? '', k.onHand,
    ]);
    const csv = [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ready-stock-page-${page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const rootStyle = {
    ...Object.fromEntries(
      Object.entries(THEME[darkMode ? 'dark' : 'light']).map(([k, v]) => [`--${k}`, v])
    ),
    '--primary': PRIMARY,
  };

  // The Drawer panel itself portals to <body>, outside the element rootStyle
  // declares the variables on, so it needs the plain hex.
  const panelBg = THEME[darkMode ? 'dark' : 'light'].surface;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);
  const hasActiveFilters =
    productTypeId !== null ||
    productFormationId !== null ||
    fabricTypeId !== null ||
    fabricColorId !== null ||
    fabricPatternId !== null ||
    locationId !== null ||
    lowStockOnly ||
    sittingOnly;
  const showEmpty = !loading && kinds.length === 0;

  const activePreset = useMemo(() => {
    if (sittingOnly) return 'sitting';
    if (lowStockOnly) return 'lowStock';
    if (locationId === 'none') return 'unplaced';
    if (
      locationId === null &&
      !lowStockOnly &&
      !sittingOnly &&
      inStockOnly &&
      productTypeId === null &&
      productFormationId === null &&
      fabricTypeId === null &&
      fabricColorId === null &&
      fabricPatternId === null &&
      !searchText
    ) {
      return 'all';
    }
    return 'custom';
  }, [
    sittingOnly,
    lowStockOnly,
    locationId,
    inStockOnly,
    productTypeId,
    productFormationId,
    fabricTypeId,
    fabricColorId,
    fabricPatternId,
    searchText,
  ]);

  const selectPreset = (presetKey) => {
    if (presetKey === 'all') {
      applyFilter({
        search: '',
        productTypeId: null,
        productFormationId: null,
        fabricTypeId: null,
        fabricColorId: null,
        fabricPatternId: null,
        locationId: null,
        lowStockOnly: false,
        sittingOnly: false,
        inStockOnly: true,
      });
      setSearchInput('');
    } else if (presetKey === 'unplaced') {
      applyFilter({
        locationId: 'none',
        lowStockOnly: false,
        sittingOnly: false,
        inStockOnly: true,
      });
    } else if (presetKey === 'lowStock') {
      applyFilter({
        locationId: null,
        lowStockOnly: true,
        sittingOnly: false,
        inStockOnly: true,
      });
    } else if (presetKey === 'sitting') {
      applyFilter({
        locationId: null,
        lowStockOnly: false,
        sittingOnly: true,
        inStockOnly: true,
      });
    }
  };

  const presetTabs = useMemo(() => [
    { id: 'all', label: 'All kinds', count: inStockOnly ? stats?.kindsWithStock : stats?.totalKinds },
    { id: 'unplaced', label: 'Needs placement', count: stats?.unplacedKinds ?? 0 },
    { id: 'lowStock', label: 'Low stock', count: stats?.lowStockKinds ?? 0 },
    { id: 'sitting', label: `Sitting ${stats?.sittingDays ?? 60}d+`, count: stats?.sittingKinds ?? 0 },
  ], [inStockOnly, stats]);

  const groupedKinds = useMemo(() => {
    if (!groupByType) return null;
    const map = new Map();
    kinds.forEach((kind) => {
      const typeName = kind.productType || 'Unassigned';
      if (!map.has(typeName)) {
        map.set(typeName, {
          name: typeName,
          kinds: [],
          totalUnits: 0,
        });
      }
      const group = map.get(typeName);
      group.kinds.push(kind);
      group.totalUnits += (kind.onHand || 0);
    });
    return Array.from(map.values());
  }, [kinds, groupByType]);

  const toggleGroupCollapse = (groupName) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const productTypeName = useMemo(
    () => productTypes.find((p) => p.id === productTypeId)?.name,
    [productTypeId, productTypes],
  );
  const formationName = useMemo(
    () => productFormations.find((f) => f.id === productFormationId)?.name,
    [productFormationId, productFormations],
  );
  const fabricName = useMemo(
    () => fabricTypes.find((f) => f.id === fabricTypeId)?.name,
    [fabricTypeId, fabricTypes],
  );
  const colorName = useMemo(
    () => fabrics.find((c) => c.id === fabricColorId)?.name,
    [fabricColorId, fabrics],
  );
  const patternName = useMemo(() => {
    if (fabricPatternId === 'none') return 'Solid (No pattern)';
    if (!fabricPatternId) return null;
    return filterPatterns.find((p) => p.id === fabricPatternId)?.name || 'Pattern';
  }, [fabricPatternId, filterPatterns]);

  const locationName = useMemo(
    () => (locationId === 'none' ? 'Not placed' : stockLocations.find((l) => l.id === locationId)?.name),
    [locationId, stockLocations],
  );

  const isColumnFilterActive = (key) => {
    if (key === 'kind') return productTypeId !== null || productFormationId !== null;
    if (key === 'fabric') return fabricTypeId !== null || fabricPatternId !== null;
    if (key === 'color') return fabricColorId !== null;
    if (key === 'location') return locationId !== null;
    return false;
  };

  const renderColumnFilter = (filterKey) => {
    if (filterKey === 'kind') {
      return (
        <KindColumnFilter
          productTypes={productTypes}
          productTypeId={productTypeId}
          productFormations={productFormations}
          productFormationId={productFormationId}
          onApply={applyFilter}
          darkMode={darkMode}
        />
      );
    }
    if (filterKey === 'fabric') {
      return (
        <FabricColumnFilter
          fabricTypes={fabricTypes}
          fabricTypeId={fabricTypeId}
          filterPatterns={filterPatterns}
          fabricPatternId={fabricPatternId}
          onApply={applyFilter}
          darkMode={darkMode}
        />
      );
    }
    if (filterKey === 'color') {
      return (
        <MenuList
          items={[{ key: null, label: 'All colors' }, ...fabrics.map((c) => ({ key: c.id, label: c.name, swatch: c.name }))]}
          active={fabricColorId}
          onSelect={(v) => applyFilter({ fabricColorId: v })}
          darkMode={darkMode}
        />
      );
    }
    if (filterKey === 'location') {
      return (
        <MenuList
          items={[
            { key: null, label: 'All locations' },
            ...stockLocations.map((l) => ({ key: l.id, label: l.name })),
            { key: 'none', label: 'Not placed' },
          ]}
          active={locationId}
          onSelect={(v) => applyFilter({ locationId: v })}
          hint={stockLocations.length === 0 ? 'No stock locations yet — add them in Product Settings.' : undefined}
          darkMode={darkMode}
        />
      );
    }
    return null;
  };

  const entryNext = entryKind
    ? entryKind.onHand + (entryMode === 'adjust' ? (entryQty || 0) : Math.abs(entryQty || 0))
    : 0;
  const entryNegative = entryMode === 'adjust' && (entryQty || 0) < 0;

  const renderDesktopRow = (kind) => {
    const sitting = kind.onHand > 0 && (kind.daysSinceMovement ?? 999) >= (stats?.sittingDays ?? 60);
    const meta = movementMeta(kind.lastMovementType);
    return (
      <div
        key={kind.id}
        className="et-stock__row"
        style={{
          display: 'grid', gridTemplateColumns: GRID, borderBottom: '1px solid var(--border)',
          background: sitting ? 'rgba(251,191,36,.04)' : 'transparent',
          opacity: kind.onHand > 0 ? 1 : 0.72,
        }}
      >
        <div style={{ padding: '9px 16px', display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
          <span style={{ flex: 'none', width: 32, height: 32, borderRadius: 9, background: kind.onHand > 0 ? hexToRgba(PRIMARY, .16) : 'var(--surface2)', color: kind.onHand > 0 ? 'var(--activeText)' : 'var(--muted)', font: '600 11px monospace', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {initials(kind.productType)}
          </span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', font: '600 13px Outfit, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {kind.productType}
              {sitting && <span style={{ font: '600 10.5px Outfit, sans-serif', color: STATUS.warn, background: 'rgba(251,191,36,.14)', padding: '2px 7px', borderRadius: 8, marginLeft: 6 }}>Sitting {kind.daysSinceMovement ?? '—'}d</span>}
            </span>
            <span style={{ display: 'block', font: '400 11.5px monospace', color: 'var(--dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {kind.productFormation}
            </span>
          </span>
        </div>
        <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center' }}>
          <span style={{ font: '600 11px Outfit, sans-serif', color: '#a78bfa', background: 'rgba(139,92,246,.14)', padding: '3px 8px', borderRadius: 5 }}>{kind.fabricType}</span>
        </div>
        <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          <ColorSwatch name={kind.fabricColor} />
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', font: '400 12.5px Outfit, sans-serif', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {kind.fabricColor}
            </span>
            {kind.fabricPattern && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 2, maxWidth: '100%' }}>
                {kind.fabricPatternUrl && (
                  <Popover
                    placement="right"
                    content={
                      <div style={{ padding: 4, textAlign: 'center' }}>
                        <img
                          src={kind.fabricPatternUrl}
                          alt={kind.fabricPattern}
                          style={{ width: 140, height: 140, objectFit: 'cover', borderRadius: 8, display: 'block', border: '1px solid var(--border)' }}
                        />
                        <div style={{ font: '600 12px Outfit, sans-serif', marginTop: 6, color: 'var(--text)' }}>{kind.fabricPattern}</div>
                      </div>
                    }
                  >
                    <img
                      src={kind.fabricPatternUrl}
                      alt={kind.fabricPattern}
                      style={{ width: 18, height: 18, borderRadius: 4, objectFit: 'cover', border: '1px solid var(--border)', flexShrink: 0, cursor: 'pointer' }}
                    />
                  </Popover>
                )}
                <span style={{ font: '600 11px Outfit, sans-serif', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {kind.fabricPattern}
                </span>
              </span>
            )}
          </span>
        </div>
        <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          <InlinePlacement
            kind={kind}
            stockLocations={stockLocations}
            canManage={canManage}
            onSave={handleInlinePlacementSave}
          />
        </div>
        <div style={{ padding: '9px 8px', display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          {kind.lastMovementType ? (
            <>
              <span style={{ font: '600 10.5px monospace', color: meta.tint, background: meta.bg || `${meta.tint}24`, padding: '2px 6px', borderRadius: 5, whiteSpace: 'nowrap' }}>{meta.label}</span>
              <span style={{ font: '400 12px monospace', color: sitting ? STATUS.warn : 'var(--muted)' }}>{fmtAgo(kind.daysSinceMovement)}</span>
            </>
          ) : (
            <span style={{ font: '400 12px Outfit, sans-serif', color: 'var(--dim)' }}>Never moved</span>
          )}
        </div>
        <div style={{ padding: '9px 8px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center' }}>
          <span style={{ font: '700 15px monospace', color: kind.onHand > 5 ? STATUS.ok : kind.onHand > 0 ? STATUS.warn : 'var(--dim)', lineHeight: 1.1 }}>
            {kind.onHand}
          </span>
          {kind.onHand === 0 ? (
            <span style={{ font: '600 10px Outfit, sans-serif', color: 'var(--dim)', marginTop: 3 }}>Out of stock</span>
          ) : kind.onHand <= 5 ? (
            <span style={{ font: '600 10px Outfit, sans-serif', color: STATUS.warn, background: 'rgba(251,191,36,.14)', padding: '1px 5px', borderRadius: 4, marginTop: 3, whiteSpace: 'nowrap' }}>
              Low stock
            </span>
          ) : null}
        </div>
        <div style={{ padding: '7px 16px 7px 8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 7 }}>
          <button type="button" className="et-stock__ghost" onClick={() => openHistory(kind)} style={rowBtn}>History</button>
          {canManage && <button type="button" onClick={() => openEntry(kind, 'add')} style={rowBtnPrimary}>+ Add</button>}
          {canManage && (
            <button type="button" className="et-stock__ghost" title="Set location" onClick={() => openPlacement(kind)} style={{ ...rowBtn, width: 30, padding: 0, justifyContent: 'center', color: 'var(--muted)' }}>⇅</button>
          )}
        </div>
      </div>
    );
  };

  const renderMobileCard = (kind) => {
    const sitting = kind.onHand > 0 && (kind.daysSinceMovement ?? 999) >= (stats?.sittingDays ?? 60);
    const meta = movementMeta(kind.lastMovementType);
    return (
      <div
        key={kind.id}
        className="et-stock__card"
        style={{
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
          padding: '12px 13px', display: 'flex', flexDirection: 'column', gap: 10,
          boxShadow: sitting ? `inset 3px 0 0 ${STATUS.warn}` : 'none',
          opacity: kind.onHand > 0 ? 1 : 0.72,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ font: '600 14px Outfit, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {kind.productType} · {kind.productFormation}
            </div>
            <div style={{ font: '400 11.5px monospace', color: 'var(--dim)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {kind.fabricType} · {kind.fabricColor}
              {kind.fabricPattern ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, margin: '0 4px' }}>
                  · {kind.fabricPatternUrl && (
                    <img
                      src={kind.fabricPatternUrl}
                      alt={kind.fabricPattern}
                      style={{ width: 14, height: 14, borderRadius: 3, objectFit: 'cover' }}
                    />
                  )}
                  {kind.fabricPattern}
                </span>
              ) : ''} · <span style={{ color: 'var(--muted)' }}>
                {kind.location || (kind.bag || kind.bin) ? (
                  <>
                    {kind.location}
                    {kind.location && (kind.bag || kind.bin) ? ' · ' : ''}
                    {(kind.bag || kind.bin) && <span style={{ color: '#f59e0b', fontWeight: 600 }}>Bag {kind.bag || kind.bin}</span>}
                  </>
                ) : 'Not placed'}
              </span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ font: '700 20px monospace', color: kind.onHand > 5 ? STATUS.ok : kind.onHand > 0 ? STATUS.warn : 'var(--dim)', lineHeight: 1 }}>{kind.onHand}</div>
            <div style={{ font: '600 10.5px Outfit, sans-serif', color: kind.onHand === 0 ? 'var(--dim)' : kind.onHand <= 5 ? STATUS.warn : 'var(--dim)', marginTop: 3 }}>
              {kind.onHand === 0 ? 'Out of stock' : kind.onHand <= 5 ? 'Low stock' : 'on hand'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {sitting ? (
            <span style={{ font: '600 10.5px Outfit, sans-serif', color: STATUS.warn, background: 'rgba(251,191,36,.14)', padding: '3px 8px', borderRadius: 8 }}>Sitting {kind.daysSinceMovement}d</span>
          ) : kind.lastMovementType ? (
            <span style={{ font: '600 10.5px monospace', color: meta.tint, background: meta.bg || `${meta.tint}24`, padding: '3px 7px', borderRadius: 5 }}>{meta.label}</span>
          ) : (
            <span style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)' }}>Never moved</span>
          )}
          {kind.daysSinceMovement !== null && kind.daysSinceMovement !== undefined && (
            <span style={{ marginLeft: 'auto', font: '400 11.5px monospace', color: 'var(--dim)' }}>{fmtAgo(kind.daysSinceMovement)} ago</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
          <button type="button" onClick={() => openHistory(kind)} style={mobileActionBtn}>History</button>
          {canManage && (
            <button type="button" onClick={() => openEntry(kind, 'add')} style={{ ...mobileActionBtn, border: `1px solid ${hexToRgba(PRIMARY, .4)}`, background: 'var(--activeBg)', color: 'var(--activeText)' }}>+ Add</button>
          )}
          {canManage && (
            <button type="button" title="Set location" onClick={() => openPlacement(kind)} style={{ ...mobileActionBtn, flex: 'none', width: 44, color: 'var(--muted)' }}>⇅</button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="et-stock" style={rootStyle}>
      <style>{`
        .et-stock{font-family:Outfit,system-ui,sans-serif;color:var(--text);display:flex;flex-direction:column;gap:14px;height:100%;min-height:0}
        .et-stock__row:hover{background:var(--hover)}
        .et-stock__card:hover{border-color:var(--borderStrong)}
        .et-stock__ghost:hover{background:var(--hover)}
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ font: '700 22px Outfit, sans-serif', letterSpacing: '-.01em' }}>Ready Stock</div>
          <div style={{ font: '400 13px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
            Finished units on hand, per product kind. Company-wide across teams.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="et-stock__ghost" title="Export this page as CSV" onClick={handleExport} style={ghostBtn}>
            <DownloadOutlined style={{ color: 'var(--muted)', fontSize: 12 }} /> Export
          </button>
          <button type="button" className="et-stock__ghost" onClick={openLedger} style={ghostBtn}>
            <HistoryOutlined style={{ color: 'var(--muted)', fontSize: 12 }} /> All movements
          </button>
          {canManage && (
            <button
              type="button"
              onClick={openNewStockModal}
              style={primaryBtn}
            >
              + Add stock
            </button>
          )}
        </div>
      </div>

      {/* KPI tiles */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 14 }}>
          <StatTile
            icon="▦" tint={PRIMARY} label="Units on hand"
            value={stats.unitsOnHand} deltaPct={stats.unitsOnHandChangePct} deltaGoodWhenUp
            foot={`across ${stats.kindsWithStock} product kind${stats.kindsWithStock === 1 ? '' : 's'}`}
          />
          <StatTile
            icon="↩" tint={STATUS.ok} label={`Returned in · ${stats.windowDays}d`}
            value={stats.returnedIn} deltaPct={stats.returnedInChangePct}
            foot={`from ${stats.returnedInOrders} order${stats.returnedInOrders === 1 ? '' : 's'}`}
          />
          <StatTile
            icon="⇄" tint="#a78bfa" label={`Pulled to orders · ${stats.windowDays}d`}
            value={stats.pulledOut} deltaPct={stats.pulledOutChangePct} deltaGoodWhenUp
            foot="sold straight from stock"
          />
          <StatTile
            icon="◷" tint={STATUS.warn} label={`Sitting ${stats.sittingDays}d+`}
            value={stats.sittingUnits}
            note={stats.sittingPctOfStock !== null ? `${stats.sittingPctOfStock}% of stock` : null}
            foot={stats.sittingKinds
              ? `${stats.sittingKinds} kind${stats.sittingKinds === 1 ? '' : 's'}${stats.sittingSince ? ` untouched since ${dayjs(stats.sittingSince).format('D MMM')}` : ' never moved'}`
              : 'nothing sitting idle'}
            active={sittingOnly}
            onClick={() => applyFilter({ sittingOnly: !sittingOnly, lowStockOnly: false, inStockOnly: true })}
          />
        </div>
      )}

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>

          {/* Operational Preset Tabs */}
          <div style={{
            padding: '10px 18px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--surface2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <span style={{ font: '700 11px Outfit, sans-serif', color: 'var(--dim)', letterSpacing: '.05em', textTransform: 'uppercase', marginRight: 4, flexShrink: 0 }}>
                Views:
              </span>
              {presetTabs.map((p) => {
                const active = activePreset === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => selectPreset(p.id)}
                    style={{
                      height: 28,
                      padding: '0 11px',
                      borderRadius: 14,
                      border: `1px solid ${active ? hexToRgba(PRIMARY, .5) : 'var(--border)'}`,
                      background: active ? 'var(--activeBg)' : 'var(--surface)',
                      color: active ? 'var(--activeText)' : 'var(--text)',
                      font: '600 12px Outfit, sans-serif',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 7,
                      whiteSpace: 'nowrap',
                      transition: 'all .15s ease',
                    }}
                  >
                    <span>{p.label}</span>
                    {p.count !== undefined && p.count !== null && (
                      <span style={{
                        font: '700 10.5px monospace',
                        background: active ? PRIMARY : 'var(--surface2)',
                        color: active ? '#fff' : 'var(--muted)',
                        padding: '1px 6px',
                        borderRadius: 8,
                        lineHeight: 1.2,
                      }}>
                        {p.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {activePreset === 'custom' && (
              <span style={{ font: '600 11.5px Outfit, sans-serif', color: 'var(--dim)', fontStyle: 'italic' }}>
                Custom filters active
              </span>
            )}
          </div>

          {/* Toolbar */}
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <div style={{ font: '700 14.5px Outfit, sans-serif' }}>Product kinds</div>
              <div style={{ font: '600 11.5px monospace', color: 'var(--dim)', padding: '2px 7px', borderRadius: 9, background: 'var(--surface2)' }}>{total}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Input
                ref={searchBoxRef}
                allowClear
                placeholder="Search product, fabric, color, formation or location"
                prefix={<SearchOutlined style={{ color: 'var(--dim)' }} />}
                suffix={<span style={{ font: '600 10.5px monospace', color: 'var(--dim)', padding: '2px 6px', borderRadius: 5, background: 'var(--surface)', border: '1px solid var(--border)' }}>/</span>}
                style={{ width: isMobile ? '100%' : 320 }}
                value={searchInput}
                // Emptying the box re-runs the query — antd 5.4's allowClear
                // has no onClear callback, it just fires onChange with ''.
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  if (e.target.value === '' && searchText !== '') applyFilter({ search: '' });
                }}
                onPressEnter={() => applyFilter({ search: searchInput })}
              />
              {isMobile && (
                <button
                  type="button"
                  onClick={() => setOpenFilter('mobileFilters')}
                  style={getFilterBtnStyle(hasActiveFilters)}
                >
                  <FilterOutlined style={{ fontSize: 11 }} /> Filter
                  {hasActiveFilters && (
                    <span style={{ font: '700 10.5px monospace', background: PRIMARY, color: '#fff', padding: '1px 5px', borderRadius: 6 }}>
                      {[
                        productTypeId !== null,
                        productFormationId !== null,
                        fabricTypeId !== null,
                        fabricPatternId !== null,
                        fabricColorId !== null,
                        locationId !== null,
                        lowStockOnly,
                        sittingOnly,
                      ].filter(Boolean).length}
                    </span>
                  )}
                </button>
              )}

              <div
                role="button" tabIndex={0}
                onClick={() => applyFilter({ inStockOnly: !inStockOnly })}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilter({ inStockOnly: !inStockOnly }); }}
                style={{
                  height: 32, padding: '0 11px', borderRadius: 8,
                  border: `1px solid ${inStockOnly ? hexToRgba(PRIMARY, .35) : 'var(--border)'}`,
                  background: inStockOnly ? 'var(--activeBg)' : 'var(--surface2)',
                  display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
                }}
              >
                <span style={{ width: 26, height: 15, borderRadius: 8, background: inStockOnly ? PRIMARY : 'var(--borderStrong)', display: 'flex', alignItems: 'center', justifyContent: inStockOnly ? 'flex-end' : 'flex-start', padding: '0 2px' }}>
                  <span style={{ width: 11, height: 11, borderRadius: 6, background: '#fff' }} />
                </span>
                <span style={{ font: '600 12px Outfit, sans-serif', color: inStockOnly ? 'var(--activeText)' : 'var(--muted)' }}>In stock only</span>
              </div>

              <div
                role="button" tabIndex={0}
                onClick={() => {
                  const next = !groupByType;
                  setGroupByType(next);
                  fetchStock({ groupByType: next });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const next = !groupByType;
                    setGroupByType(next);
                    fetchStock({ groupByType: next });
                  }
                }}
                title="Group table rows by product type"
                style={{
                  height: 32, padding: '0 11px', borderRadius: 8,
                  border: `1px solid ${groupByType ? hexToRgba(PRIMARY, .35) : 'var(--border)'}`,
                  background: groupByType ? 'var(--activeBg)' : 'var(--surface2)',
                  display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
                }}
              >
                <span style={{ width: 26, height: 15, borderRadius: 8, background: groupByType ? PRIMARY : 'var(--borderStrong)', display: 'flex', alignItems: 'center', justifyContent: groupByType ? 'flex-end' : 'flex-start', padding: '0 2px' }}>
                  <span style={{ width: 11, height: 11, borderRadius: 6, background: '#fff' }} />
                </span>
                <span style={{ font: '600 12px Outfit, sans-serif', color: groupByType ? 'var(--activeText)' : 'var(--muted)' }}>Group by type</span>
              </div>
            </div>
          </div>

          {/* Filter chips */}
          {hasActiveFilters && (
            <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ font: '600 11px Outfit, sans-serif', color: 'var(--dim)', letterSpacing: '.05em', textTransform: 'uppercase', marginRight: 2 }}>Filters</span>
              {productTypeId !== null && <Chip label="Type" value={productTypeName} onClear={() => applyFilter({ productTypeId: null })} />}
              {productFormationId !== null && <Chip label="Formation" value={formationName} onClear={() => applyFilter({ productFormationId: null })} />}
              {fabricTypeId !== null && <Chip label="Fabric" value={fabricName} onClear={() => applyFilter({ fabricTypeId: null })} />}
              {fabricPatternId !== null && <Chip label="Pattern" value={patternName} onClear={() => applyFilter({ fabricPatternId: null })} />}
              {fabricColorId !== null && (
                <Chip
                  label="Color"
                  value={
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <ColorSwatch name={colorName} />
                      {colorName}
                    </span>
                  }
                  onClear={() => applyFilter({ fabricColorId: null })}
                />
              )}
              {locationId !== null && <Chip label="Location" value={locationName} onClear={() => applyFilter({ locationId: null })} />}
              {lowStockOnly && <Chip label="Stock" value="Low (≤5)" tone="warn" onClear={() => applyFilter({ lowStockOnly: false })} />}
              {sittingOnly && <Chip label="Sitting" value={`${stats?.sittingDays ?? 60}d+`} tone="warn" onClear={() => applyFilter({ sittingOnly: false })} />}
              <button type="button" onClick={clearAllFilters} style={{ marginLeft: 'auto', height: 26, padding: '0 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)', font: '600 12px Outfit, sans-serif', cursor: 'pointer' }}>
                Clear all
              </button>
            </div>
          )}

          {/* Desktop table */}
          {!isMobile && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <div style={{ minWidth: 940 }}>
                <div style={{ display: 'grid', gridTemplateColumns: GRID, background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                  {[
                    { key: 'kind', label: 'Product kind', filter: 'kind', pad: '9px 16px' },
                    { key: 'fabric', label: 'Fabric', filter: 'fabric' },
                    { key: 'color', label: 'Color', filter: 'color' },
                    { key: 'location', label: 'Location & Bag', filter: 'location' },
                    { key: 'lastMovement', label: 'Last movement', sortable: true },
                    { key: 'onHand', label: 'On hand', sortable: true, align: 'flex-end' },
                    { key: 'action', label: 'Action', align: 'flex-end', pad: '9px 16px 9px 8px' },
                  ].map((h) => {
                    const sorted = sortField === h.key;
                    const active = isColumnFilterActive(h.filter);
                    return (
                      <div
                        key={h.key}
                        style={{
                          padding: h.pad || '9px 8px', display: 'flex', alignItems: 'center', gap: 5,
                          justifyContent: h.align || 'flex-start',
                          font: '700 11.5px Outfit, sans-serif',
                          color: sorted || active ? 'var(--activeText)' : 'var(--muted)',
                          letterSpacing: '.05em', textTransform: 'uppercase',
                        }}
                      >
                        <span
                          onClick={h.sortable ? () => toggleSort(h.key) : undefined}
                          title={h.sortable ? `Sort by ${h.label}` : h.label}
                          style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: h.sortable ? 'pointer' : 'default', minWidth: 0 }}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.label}</span>
                          {h.sortable && (
                            sorted
                              ? <span style={{ color: PRIMARY, fontSize: 9 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                              : <span style={{ color: 'var(--dim)', fontSize: 9 }}>⇅</span>
                          )}
                        </span>
                        {h.filter && (
                          <Popover
                            trigger="click"
                            placement="bottom"
                            open={openFilter === h.filter}
                            onOpenChange={(o) => setOpenFilter(o ? h.filter : null)}
                            content={renderColumnFilter(h.filter)}
                          >
                            <span
                              onClick={(e) => e.stopPropagation()}
                              title={`Filter ${h.label}`}
                              style={{
                                marginLeft: 'auto', flexShrink: 0, cursor: 'pointer', padding: '2px 4px',
                                borderRadius: 4,
                                background: active ? hexToRgba(PRIMARY, .15) : 'transparent',
                                color: active ? PRIMARY : 'var(--dim)',
                                fontSize: 11,
                                display: 'flex', alignItems: 'center',
                              }}
                            >
                              <FilterOutlined />
                            </span>
                          </Popover>
                        )}
                      </div>
                    );
                  })}
                </div>

                {loading && <LoadingRow />}

                {showEmpty && (
                  <EmptyState
                    search={searchText} inStockOnly={inStockOnly} hasFilters={hasActiveFilters}
                    onShowAll={() => applyFilter({ inStockOnly: false })}
                    onClear={() => { setSearchInput(''); clearAllFilters(); }}
                    onAddStock={openNewStockModal}
                    canManage={canManage}
                  />
                )}

                {!loading && (
                  groupByType && groupedKinds ? (
                    groupedKinds.map((group) => {
                      const isCollapsed = Boolean(collapsedGroups[group.name]);
                      return (
                        <React.Fragment key={group.name}>
                          <div
                            onClick={() => toggleGroupCollapse(group.name)}
                            style={{
                              padding: '8px 16px',
                              background: 'var(--surface2)',
                              borderBottom: '1px solid var(--border)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              cursor: 'pointer',
                              userSelect: 'none',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                              <span style={{
                                fontSize: 10,
                                color: 'var(--dim)',
                                transition: 'transform 0.2s ease',
                                transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                display: 'inline-block',
                              }}>
                                ▼
                              </span>
                              <span style={{
                                width: 22, height: 22, borderRadius: 6,
                                background: hexToRgba(PRIMARY, .14), color: 'var(--activeText)',
                                font: '700 10.5px monospace', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                {initials(group.name)}
                              </span>
                              <span style={{ font: '700 13px Outfit, sans-serif', color: 'var(--text)', letterSpacing: '-.01em' }}>
                                {group.name}
                              </span>
                              <span style={{
                                font: '600 11px Outfit, sans-serif',
                                color: 'var(--muted)',
                                background: 'var(--surface)',
                                border: '1px solid var(--border)',
                                padding: '1px 8px',
                                borderRadius: 10,
                              }}>
                                {group.kinds.length} {group.kinds.length === 1 ? 'kind' : 'kinds'} · {group.totalUnits} units on hand
                              </span>
                            </div>
                            <span style={{ font: '600 11px Outfit, sans-serif', color: 'var(--dim)' }}>
                              {isCollapsed ? 'Expand' : 'Collapse'}
                            </span>
                          </div>
                          {!isCollapsed && group.kinds.map(renderDesktopRow)}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    kinds.map(renderDesktopRow)
                  )
                )}
              </div>
            </div>
          )}

          {/* Mobile card list */}
          {isMobile && (
            <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 9 }}>
              {loading && <LoadingRow />}
              {showEmpty && (
                <EmptyState
                  search={searchText} inStockOnly={inStockOnly} hasFilters={hasActiveFilters}
                  onShowAll={() => applyFilter({ inStockOnly: false })}
                  onClear={() => { setSearchInput(''); clearAllFilters(); }}
                  onAddStock={openNewStockModal}
                  canManage={canManage}
                />
              )}
              {!loading && (
                groupByType && groupedKinds ? (
                  groupedKinds.map((group) => {
                    const isCollapsed = Boolean(collapsedGroups[group.name]);
                    return (
                      <div key={group.name} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div
                          onClick={() => toggleGroupCollapse(group.name)}
                          style={{
                            padding: '10px 14px',
                            background: 'var(--surface2)',
                            borderRadius: 10,
                            border: '1px solid var(--border)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            userSelect: 'none',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{
                              fontSize: 10,
                              color: 'var(--dim)',
                              transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                              display: 'inline-block',
                              transition: 'transform 0.2s ease',
                            }}>
                              ▼
                            </span>
                            <span style={{ font: '700 13.5px Outfit, sans-serif', color: 'var(--text)' }}>
                              {group.name}
                            </span>
                            <span style={{ font: '600 11px Outfit, sans-serif', color: 'var(--muted)', background: 'var(--surface)', padding: '1px 7px', borderRadius: 8, border: '1px solid var(--border)' }}>
                              {group.kinds.length} · {group.totalUnits}u
                            </span>
                          </div>
                          <span style={{ font: '600 11px Outfit, sans-serif', color: 'var(--dim)' }}>
                            {isCollapsed ? 'Expand' : 'Collapse'}
                          </span>
                        </div>
                        {!isCollapsed && group.kinds.map(renderMobileCard)}
                      </div>
                    );
                  })
                ) : (
                  kinds.map(renderMobileCard)
                )
              )}
            </div>
          )}

          {/* Footer */}
          <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>
                {total === 0 ? '0 kinds' : (
                  <>
                    Showing <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{rangeStart}–{rangeEnd}</span>{' '}
                    of <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{total}</span> kinds ·{' '}
                    <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{matchedUnits}</span> units
                  </>
                )}
              </div>
              <Popover
                trigger="click"
                open={openFilter === 'rows'}
                onOpenChange={(o) => setOpenFilter(o ? 'rows' : null)}
                content={<MenuList items={PAGE_SIZE_OPTIONS.map((n) => ({ key: n, label: String(n) }))} active={pageSize} onSelect={(v) => applyFilter({ pageSize: v })} darkMode={darkMode} />}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
                  <span style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>Rows</span>
                  <span style={{ height: 28, padding: '0 9px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', font: '600 12px monospace', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {pageSize}<DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
                  </span>
                </span>
              </Popover>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <PageBtn disabled={page <= 1} onClick={() => goToPage(page - 1)}>‹</PageBtn>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .map((p, i, arr) => (
                  <React.Fragment key={p}>
                    {i > 0 && arr[i - 1] !== p - 1 && <span style={{ color: 'var(--dim)', fontSize: 12 }}>…</span>}
                    <PageBtn active={p === page} onClick={() => goToPage(p)}>{p}</PageBtn>
                  </React.Fragment>
                ))}
              <PageBtn disabled={page >= totalPages} onClick={() => goToPage(page + 1)}>›</PageBtn>
            </div>
          </div>
        </div>

        {/* History drawer — one Product Kind's ledger */}
        <Drawer
          open={!!historyKind}
          onClose={() => setHistoryKind(null)}
          width={isMobile ? '100%' : 520}
          closable={false}
          bodyStyle={{ padding: 0, background: panelBg }}
          drawerStyle={{ background: panelBg }}
        >
          <div style={rootStyle}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ font: '700 14.5px Outfit, sans-serif', color: 'var(--text)' }}>History</div>
                <div style={{ font: '400 11.5px monospace', color: 'var(--dim)', marginTop: 3 }}>
                  {historyKind && [historyKind.productType, historyKind.fabricType, historyKind.fabricColor, historyKind.fabricPattern, historyKind.productFormation].filter(Boolean).join(' / ')}
                </div>
              </div>
              <span onClick={() => setHistoryKind(null)} style={{ width: 26, height: 26, borderRadius: 7, background: 'var(--surface2)', color: 'var(--muted)', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <CloseOutlined />
              </span>
            </div>

            {historySummary && (
              <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg)', display: 'flex', alignItems: 'center', gap: 16 }}>
                <SummaryStat label="On hand" value={historySummary.onHand} tint={STATUS.ok} />
                <span style={{ width: 1, height: 28, background: 'var(--border)' }} />
                <SummaryStat label={`In · ${historySummary.windowDays}d`} value={`+${historySummary.in}`} tint="var(--text)" />
                <span style={{ width: 1, height: 28, background: 'var(--border)' }} />
                <SummaryStat label={`Out · ${historySummary.windowDays}d`} value={historySummary.out} tint={STATUS.errorSoft} />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) 70px minmax(0,1.2fr) 90px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
              {['Type', 'Qty', 'By', 'When'].map((h, i) => (
                <div key={h} style={{ padding: i === 0 ? '10px 16px' : i === 3 ? '10px 16px 10px 8px' : '10px 8px', textAlign: i === 1 ? 'right' : 'left', font: '700 11px Outfit, sans-serif', color: 'var(--muted)', letterSpacing: '.05em', textTransform: 'uppercase' }}>{h}</div>
              ))}
            </div>

            {historyLoading && <LoadingRow />}
            {!historyLoading && movements.length === 0 && (
              <div style={{ padding: '32px 18px', textAlign: 'center', font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>
                No movements recorded for this kind yet.
              </div>
            )}
            {movements.map((m) => <MovementRow key={m.id} movement={m} />)}
          </div>
        </Drawer>

        {/* All-movements drawer — the company-wide ledger */}
        <Drawer
          open={ledgerOpen}
          onClose={() => setLedgerOpen(false)}
          width={isMobile ? '100%' : 560}
          closable={false}
          bodyStyle={{ padding: 0, background: panelBg }}
          drawerStyle={{ background: panelBg }}
        >
          <div style={rootStyle}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ font: '700 14.5px Outfit, sans-serif', color: 'var(--text)' }}>All movements</div>
                <div style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)', marginTop: 3 }}>The 50 most recent entries, company-wide.</div>
              </div>
              <span onClick={() => setLedgerOpen(false)} style={{ width: 26, height: 26, borderRadius: 7, background: 'var(--surface2)', color: 'var(--muted)', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <CloseOutlined />
              </span>
            </div>
            {ledgerLoading && <LoadingRow />}
            {!ledgerLoading && ledger.length === 0 && (
              <div style={{ padding: '32px 18px', textAlign: 'center', font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>
                Nothing has moved through ready stock yet.
              </div>
            )}
            {ledger.map((m) => <MovementRow key={m.id} movement={m} showKind />)}
          </div>
        </Drawer>

        {/* Add / adjust sheet */}
        <Sheet
          open={!!entryKind}
          onClose={() => setEntryKind(null)}
          rootStyle={rootStyle}
          title="Add / adjust stock"
          header={
            <div style={{ display: 'flex', padding: 3, borderRadius: 9, background: 'var(--surface2)', border: '1px solid var(--border)', gap: 2 }}>
              {[['add', 'Add'], ['adjust', 'Adjust']].map(([mode, label]) => (
                <span
                  key={mode}
                  onClick={() => { setEntryMode(mode); setEntryQty(mode === 'adjust' ? -1 : 1); }}
                  style={{
                    height: 26, padding: '0 12px', borderRadius: 7,
                    background: entryMode === mode ? PRIMARY : 'transparent',
                    color: entryMode === mode ? '#fff' : 'var(--muted)',
                    font: '600 12px Outfit, sans-serif', display: 'flex', alignItems: 'center', cursor: 'pointer',
                  }}
                >
                  {label}
                </span>
              ))}
            </div>
          }
        >
          {entryKind && (
            <>
              {/* Which kind, and where it lives — the same identity line the mock shows */}
              <div style={{ padding: '11px 13px', borderRadius: 11, background: 'var(--surface2)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ font: '600 13px Outfit, sans-serif' }}>{entryKind.productType} · {entryKind.productFormation}</div>
                  <div style={{ font: '400 11.5px monospace', color: 'var(--dim)', marginTop: 2 }}>
                    {entryKind.fabricType} · {entryKind.fabricColor}{entryKind.fabricPattern ? ` · ${entryKind.fabricPattern}` : ''} · <span style={{ color: 'var(--muted)' }}>
                      {entryKind.location || (entryKind.bag || entryKind.bin) ? (
                        <>
                          {entryKind.location}
                          {entryKind.location && (entryKind.bag || entryKind.bin) ? ' · ' : ''}
                          {(entryKind.bag || entryKind.bin) && `Bag ${entryKind.bag || entryKind.bin}`}
                        </>
                      ) : 'Not placed'}
                    </span>
                  </div>
                </div>
                <div style={{ textAlign: 'right', flex: 'none' }}>
                  <div style={{ font: '600 10.5px Outfit, sans-serif', color: 'var(--dim)', letterSpacing: '.05em', textTransform: 'uppercase' }}>On hand</div>
                  <div style={{ font: '700 16px monospace', color: STATUS.ok }}>{entryKind.onHand}</div>
                </div>
              </div>

              {/* Choose a different kind without leaving the sheet — the header
                  "+ Add stock" button opens here on whatever row is first. */}
              <Field label="Product kind">
                <Select
                  value={entryKind.id}
                  onChange={(id) => setEntryKind(kinds.find((k) => k.id === id) || entryKind)}
                  style={{ width: '100%' }}
                  // The sheet is a plain overlay at z-index 1100; the dropdown
                  // portals to <body> at antd's default 1050, so it needs raising.
                  dropdownStyle={{ zIndex: 1200 }}
                  showSearch
                  optionFilterProp="label"
                  options={kinds.map((k) => ({
                    value: k.id,
                    label: `${k.productType} · ${k.productFormation} · ${k.fabricType} · ${k.fabricColor}${k.fabricPattern ? ` · ${k.fabricPattern}` : ''}`,
                  }))}
                />
              </Field>

              <Field label={entryMode === 'adjust' ? 'Adjustment' : 'Quantity to add'}>
                <div style={{
                  height: 40, borderRadius: 9,
                  border: `1px solid ${entryNegative ? 'rgba(248,113,113,.4)' : hexToRgba(PRIMARY, .45)}`,
                  background: entryNegative ? 'rgba(248,113,113,.06)' : 'var(--surface2)',
                  display: 'flex', alignItems: 'center', padding: '0 6px 0 13px', gap: 10,
                  boxShadow: entryNegative ? 'none' : `0 0 0 3px ${hexToRgba(PRIMARY, .12)}`,
                }}>
                  <input
                    type="number"
                    value={entryQty}
                    onChange={(e) => setEntryQty(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                    style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', font: '700 14px monospace', color: entryNegative ? STATUS.errorSoft : 'var(--text)' }}
                  />
                  <span onClick={() => setEntryQty((q) => (Number(q) || 0) - 1)} style={stepBtn}>−</span>
                  <span onClick={() => setEntryQty((q) => (Number(q) || 0) + 1)} style={{ ...stepBtn, color: 'var(--text)' }}>+</span>
                </div>
                {entryMode === 'adjust' && (
                  <div style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--muted)', marginTop: 5 }}>
                    A negative number removes units; stock can't go below zero.
                  </div>
                )}
              </Field>

              <Field label={<>Reason <span style={{ color: 'var(--dim)', fontWeight: 400 }}>optional</span></>}>
                <Input
                  placeholder="e.g. factory batch, stock-take correction"
                  value={entryReason}
                  onChange={(e) => setEntryReason(e.target.value)}
                />
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 7 }}>
                  {REASON_PRESETS.map((r) => (
                    <span key={r} onClick={() => setEntryReason(r)} style={{
                      font: '600 11.5px Outfit, sans-serif',
                      color: entryReason === r ? 'var(--activeText)' : 'var(--muted)',
                      background: entryReason === r ? 'var(--activeBg)' : 'var(--surface2)',
                      border: `1px solid ${entryReason === r ? hexToRgba(PRIMARY, .35) : 'var(--border)'}`,
                      padding: '4px 9px', borderRadius: 7, cursor: 'pointer',
                    }}>{r}</span>
                  ))}
                </div>
              </Field>

              <div style={{
                padding: '11px 13px', borderRadius: 11,
                background: entryNegative ? 'rgba(248,113,113,.06)' : 'rgba(52,211,153,.06)',
                border: `1px solid ${entryNegative ? 'rgba(248,113,113,.24)' : 'rgba(52,211,153,.24)'}`,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{ font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>After this entry</span>
                <span style={{ marginLeft: 'auto', font: '600 12.5px monospace', color: 'var(--muted)' }}>{entryKind.onHand}</span>
                <span style={{ color: 'var(--dim)', fontSize: 11 }}>→</span>
                <span style={{ font: '700 15px monospace', color: entryNegative ? STATUS.errorSoft : STATUS.ok }}>{entryNext}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9 }}>
                <button type="button" onClick={() => setEntryKind(null)} style={sheetCancelBtn}>Cancel</button>
                <button type="button" disabled={saving || entryNext < 0} onClick={submitEntry} style={{ ...sheetOkBtn, opacity: saving || entryNext < 0 ? 0.6 : 1 }}>
                  {entryMode === 'adjust' ? 'Apply adjustment' : 'Add to stock'}
                </button>
              </div>
            </>
          )}
        </Sheet>

        {/* Add new stock sheet (header button / full catalogue) */}
        <Sheet
          open={newStockOpen}
          onClose={() => setNewStockOpen(false)}
          rootStyle={rootStyle}
          width={520}
          title="Add ready stock"
        >
          <div style={{ font: '400 12.5px/1.4 Outfit, sans-serif', color: 'var(--muted)', marginTop: -4 }}>
            Record opening inventory, new factory arrivals, or add units to existing items.
          </div>

          {/* Row 1: Product Type & Formation */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            <Field label="Product type *">
              <Select
                placeholder="Select product type"
                value={newProductType ?? undefined}
                onChange={(v) => {
                  setNewProductType(v);
                  setNewFormation(null);
                }}
                showSearch
                optionFilterProp="label"
                dropdownStyle={{ zIndex: 1200 }}
                options={productTypes.map((pt) => ({ value: pt.id, label: pt.name }))}
              />
            </Field>

            <Field label="Formation *">
              <Select
                placeholder={newProductType ? "Select formation" : "Select product type first"}
                value={newFormation ?? undefined}
                onChange={(v) => setNewFormation(v)}
                showSearch
                disabled={!newProductType}
                optionFilterProp="label"
                dropdownStyle={{ zIndex: 1200 }}
                options={(newProductType ? (formationsByType[newProductType] || []) : []).map((pf) => ({ value: pf.id, label: pf.name }))}
              />
            </Field>
          </div>

          {/* Row 2: Fabric Type & Color */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            <Field label="Fabric type *">
              <Select
                placeholder="Select fabric"
                value={newFabricType ?? undefined}
                onChange={handleFabricChange}
                showSearch
                optionFilterProp="label"
                dropdownStyle={{ zIndex: 1200 }}
                options={fabricTypes.map((ft) => ({ value: ft.id, label: ft.name }))}
              />
            </Field>

            <Field label="Color *">
              <Select
                placeholder="Select color"
                value={newColor ?? undefined}
                onChange={(v) => setNewColor(v)}
                showSearch
                optionFilterProp="filterLabel"
                dropdownStyle={{ zIndex: 1200 }}
                options={fabrics.map((c) => ({
                  value: c.id,
                  filterLabel: c.name,
                  label: (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <ColorSwatch name={c.name} />
                      {c.name}
                    </span>
                  ),
                }))}
              />
            </Field>
          </div>

          {/* Row 3: Fabric Pattern (Optional) */}
          <Field label={<>Fabric pattern <span style={{ color: 'var(--dim)', fontWeight: 400 }}>optional</span></>}>
            <Select
              allowClear
              placeholder={
                !newFabricType
                  ? 'Pick a fabric type first'
                  : availablePatterns.length === 0
                  ? 'No patterns for this fabric (Solid / Plain)'
                  : 'Select pattern or leave blank for solid'
              }
              disabled={!newFabricType || availablePatterns.length === 0}
              value={newPattern ?? undefined}
              onChange={(v) => setNewPattern(v ?? null)}
              showSearch
              optionFilterProp="label"
              dropdownStyle={{ zIndex: 1200 }}
              options={availablePatterns.map((p) => ({ value: p.id, label: p.name }))}
            />
          </Field>

          {/* Live Kind Status Banner */}
          {detecting && (
            <div style={{ padding: '10px 13px', borderRadius: 9, background: 'var(--surface2)', font: '400 12px Outfit, sans-serif', color: 'var(--dim)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Spin size="small" /> Checking catalogue status...
            </div>
          )}

          {!detecting && detectedKind && (
            <div style={{
              padding: '11px 13px', borderRadius: 10,
              background: 'rgba(52,211,153,.08)', border: '1px solid rgba(52,211,153,.28)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            }}>
              <div>
                <div style={{ font: '600 12.5px Outfit, sans-serif', color: STATUS.ok, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CheckOutlined style={{ fontSize: 11 }} /> Existing in catalogue
                </div>
                <div style={{ font: '400 11.5px monospace', color: 'var(--dim)', marginTop: 2 }}>
                  {detectedKind.location || (detectedKind.bag || detectedKind.bin) ? `@ ${[detectedKind.location, (detectedKind.bag || detectedKind.bin) ? `Bag ${detectedKind.bag || detectedKind.bin}` : ''].filter(Boolean).join(' · ')}` : 'Currently not placed'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ font: '700 18px monospace', color: STATUS.ok }}>{detectedKind.onHand}</div>
                <div style={{ font: '400 10.5px Outfit, sans-serif', color: 'var(--dim)' }}>on hand</div>
              </div>
            </div>
          )}

          {!detecting && !detectedKind && newProductType && newFormation && newFabricType && newColor && (
            <div style={{
              padding: '11px 13px', borderRadius: 10,
              background: hexToRgba(PRIMARY, .08), border: `1px solid ${hexToRgba(PRIMARY, .28)}`,
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <span style={{ font: '700 14px Outfit, sans-serif', color: PRIMARY, lineHeight: 1.2 }}>✦</span>
              <div>
                <div style={{ font: '600 12.5px Outfit, sans-serif', color: 'var(--text)' }}>New Product Kind</div>
                <div style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)', marginTop: 2 }}>
                  This combination will be registered in Ready Stock with starting balance 0.
                </div>
              </div>
            </div>
          )}

          {/* Row 4: Quantity */}
          <Field label="Quantity to add *">
            <div style={{
              height: 40, borderRadius: 9,
              border: `1px solid ${hexToRgba(PRIMARY, .45)}`,
              background: 'var(--surface2)',
              display: 'flex', alignItems: 'center', padding: '0 6px 0 13px', gap: 10,
              boxShadow: `0 0 0 3px ${hexToRgba(PRIMARY, .12)}`,
            }}>
              <input
                type="number"
                min="1"
                value={newQty}
                onChange={(e) => setNewQty(e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10)))}
                style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', font: '700 14px monospace', color: 'var(--text)' }}
              />
              <span onClick={() => setNewQty((q) => Math.max(1, (Number(q) || 0) - 1))} style={stepBtn}>−</span>
              <span onClick={() => setNewQty((q) => (Number(q) || 0) + 1)} style={{ ...stepBtn, color: 'var(--text)' }}>+</span>
            </div>
          </Field>

          {/* Row 5: Reason & Presets */}
          <Field label={<>Reason / Note <span style={{ color: 'var(--dim)', fontWeight: 400 }}>optional</span></>}>
            <Input
              placeholder="e.g. Opening stock, Factory batch"
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 7 }}>
              {['Opening stock', 'Factory batch', 'Old inventory', 'Stock-take'].map((r) => (
                <span key={r} onClick={() => setNewReason(r)} style={{
                  font: '600 11.5px Outfit, sans-serif',
                  color: newReason === r ? 'var(--activeText)' : 'var(--muted)',
                  background: newReason === r ? 'var(--activeBg)' : 'var(--surface2)',
                  border: `1px solid ${newReason === r ? hexToRgba(PRIMARY, .35) : 'var(--border)'}`,
                  padding: '4px 9px', borderRadius: 7, cursor: 'pointer',
                }}>{r}</span>
              ))}
            </div>
          </Field>

          {/* Row 6: Placement (Location & Bag) */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.3fr 1fr', gap: 12 }}>
            <Field label={<>Location <span style={{ color: 'var(--dim)', fontWeight: 400 }}>optional</span></>}>
              <Select
                allowClear
                placeholder="Pick a location"
                value={newLocation ?? undefined}
                onChange={(v) => setNewLocation(v ?? null)}
                dropdownStyle={{ zIndex: 1200 }}
                options={stockLocations.map((l) => ({ value: l.id, label: l.name }))}
              />
            </Field>

            <Field label={<>Bag <span style={{ color: 'var(--dim)', fontWeight: 400 }}>optional</span></>}>
              <Input
                placeholder="e.g. Bag 12"
                value={newBag}
                onChange={(e) => setNewBag(e.target.value)}
              />
            </Field>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 9, justifyContent: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
            <button
              type="button"
              onClick={() => setNewStockOpen(false)}
              style={sheetCancelBtn}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={submitNewStock}
              style={{ ...sheetOkBtn, opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'Adding...' : `+ Add ${newQty ? `${newQty} unit${newQty === 1 ? '' : 's'}` : 'stock'}`}
            </button>
          </div>
        </Sheet>

        {/* Placement sheet — where the kind is kept */}
        <Sheet
          open={!!placementKind}
          onClose={() => setPlacementKind(null)}
          rootStyle={rootStyle}
          title="Set location & bag"
        >
          {placementKind && (
            <>
              <div style={{ padding: '11px 13px', borderRadius: 11, background: 'var(--surface2)', border: '1px solid var(--border)' }}>
                <div style={{ font: '600 13px Outfit, sans-serif' }}>{placementKind.productType} · {placementKind.productFormation}</div>
                <div style={{ font: '400 11.5px monospace', color: 'var(--dim)', marginTop: 2 }}>
                  {placementKind.fabricType} · {placementKind.fabricColor}{placementKind.fabricPattern ? ` · ${placementKind.fabricPattern}` : ''}
                </div>
              </div>

              <Field label="Location">
                <Select
                  allowClear
                  placeholder={stockLocations.length ? 'Pick a location' : 'No locations yet — add them in Product Settings'}
                  value={placementLocation}
                  onChange={(v) => setPlacementLocation(v ?? null)}
                  style={{ width: '100%' }}
                  dropdownStyle={{ zIndex: 1200 }}
                  options={stockLocations.map((l) => ({ value: l.id, label: l.name }))}
                />
              </Field>

              <Field label={<>Bag <span style={{ color: 'var(--dim)', fontWeight: 400 }}>optional</span></>}>
                <Input placeholder="e.g. Bag 12" maxLength={32} value={placementBag} onChange={(e) => setPlacementBag(e.target.value)} />
              </Field>

              <div style={{ font: '400 11.5px/1.5 Outfit, sans-serif', color: 'var(--dim)' }}>
                Placement records where these units are kept. It doesn't move any stock — on hand stays {placementKind.onHand}.
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9 }}>
                <button type="button" onClick={() => setPlacementKind(null)} style={sheetCancelBtn}>Cancel</button>
                <button type="button" disabled={saving} onClick={submitPlacement} style={{ ...sheetOkBtn, opacity: saving ? 0.6 : 1 }}>Save placement</button>
              </div>
            </>
          )}
        </Sheet>

        {/* Mobile filter sheet */}
        <Sheet
          open={openFilter === 'mobileFilters'}
          onClose={() => setOpenFilter(null)}
          rootStyle={rootStyle}
          title="Filters"
        >
          <MobileStockFilters
            productTypes={productTypes}
            productTypeId={productTypeId}
            productFormations={productFormations}
            productFormationId={productFormationId}
            fabricTypes={fabricTypes}
            fabricTypeId={fabricTypeId}
            filterPatterns={filterPatterns}
            fabricPatternId={fabricPatternId}
            fabrics={fabrics}
            fabricColorId={fabricColorId}
            stockLocations={stockLocations}
            locationId={locationId}
            onApply={(draft) => applyFilter(draft)}
            onClear={() => { clearAllFilters(); setOpenFilter(null); }}
          />
        </Sheet>
      </ConfigProvider>
    </div>
  );
}

/* ---------------------------------------------------------------------- */

const ghostBtn = {
  height: 38, padding: '0 14px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--surface)',
  color: 'var(--text)', font: '600 13px Outfit, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
};
const primaryBtn = {
  height: 38, padding: '0 18px', borderRadius: 9, border: 'none', background: PRIMARY, color: '#fff',
  font: '700 13px Outfit, sans-serif', cursor: 'pointer',
};
const filterBtn = {
  height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)',
  color: 'var(--text)', font: '600 12px Outfit, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
};
const getFilterBtnStyle = (isActive) => ({
  ...filterBtn,
  ...(isActive ? {
    border: `1px solid ${hexToRgba(PRIMARY, .4)}`,
    background: 'var(--activeBg)',
    color: 'var(--activeText)',
  } : {}),
});
const rowBtn = {
  height: 30, padding: '0 11px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)',
  color: 'var(--text)', font: '600 12px Outfit, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center',
};
const rowBtnPrimary = {
  ...rowBtn, border: `1px solid ${hexToRgba(PRIMARY, .4)}`, background: 'var(--activeBg)', color: 'var(--activeText)',
};
const mobileActionBtn = {
  flex: 1, height: 44, borderRadius: 9, border: '1px solid var(--border)', background: 'var(--surface2)',
  color: 'var(--text)', font: '600 12.5px Outfit, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const stepBtn = {
  width: 28, height: 28, borderRadius: 7, background: 'var(--surface)', border: '1px solid var(--border)',
  color: 'var(--muted)', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: 'none',
};
const sheetCancelBtn = {
  height: 34, padding: '0 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)',
  color: 'var(--muted)', font: '600 12.5px Outfit, sans-serif', cursor: 'pointer',
};
const sheetOkBtn = {
  height: 34, padding: '0 16px', borderRadius: 8, border: 'none', background: PRIMARY, color: '#fff',
  font: '700 12.5px Outfit, sans-serif', cursor: 'pointer',
};

/** A modal that adopts the shell palette, used for both the entry and placement sheets. */
function Sheet({ open, onClose, title, header, rootStyle, width = 480, children }) {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ ...rootStyle, width: `min(${width}px, 100%)`, maxHeight: '90vh', overflowY: 'auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, fontFamily: 'Outfit, system-ui, sans-serif', color: 'var(--text)' }}
      >
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ font: '700 14.5px Outfit, sans-serif' }}>{title}</div>
          {header || (
            <span onClick={onClose} style={{ width: 26, height: 26, borderRadius: 7, background: 'var(--surface2)', color: 'var(--muted)', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <CloseOutlined />
            </span>
          )}
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ font: '600 12px Outfit, sans-serif', color: 'var(--muted)' }}>{label}</div>
      {children}
    </div>
  );
}

function StatTile({ icon, tint, label, value, deltaPct, deltaGoodWhenUp, note, foot, active, onClick }) {
  const clickable = !!onClick;
  const deltaTint = deltaPct === null || deltaPct === undefined
    ? 'var(--muted)'
    : (deltaPct >= 0) === !!deltaGoodWhenUp ? STATUS.ok : STATUS.errorSoft;
  return (
    <div
      role={clickable ? 'button' : undefined} tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
      style={{
        background: 'var(--surface)', border: `1px solid ${active ? tint : 'var(--border)'}`, borderRadius: 14,
        padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5,
        cursor: clickable ? 'pointer' : 'default',
        boxShadow: active ? `0 0 0 1px ${tint}` : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ width: 19, height: 19, borderRadius: 6, background: `${tint}29`, color: tint, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>
        <span style={{ font: '600 11.5px Outfit, sans-serif', color: 'var(--muted)', letterSpacing: '.02em', textTransform: 'uppercase' }}>{label}</span>
        {note && <span style={{ font: '600 11px Outfit, sans-serif', color: 'var(--muted)', marginLeft: 'auto' }}>{note}</span>}
        {!note && deltaPct !== null && deltaPct !== undefined && (
          <span style={{ font: '600 11px Outfit, sans-serif', color: deltaTint, marginLeft: 'auto' }}>{deltaPct >= 0 ? '▲' : '▼'} {Math.abs(deltaPct)}%</span>
        )}
      </div>
      <div style={{ font: '700 18px var(--mono)', color: 'var(--text)' }}>{value?.toLocaleString?.() ?? value}</div>
      {foot && <div style={{ font: '400 11px Outfit, sans-serif', color: 'var(--dim)' }}>{foot}</div>}
    </div>
  );
}

function ColorSwatch({ name }) {
  const hex = COLOR_HEX[(name || '').toLowerCase()];
  return (
    <span
      title={name}
      style={{
        flex: 'none', width: 11, height: 11, borderRadius: 3,
        background: hex || 'transparent',
        border: hex ? '1px solid rgba(128,128,128,.28)' : '1px dashed var(--borderStrong)',
      }}
    />
  );
}

/** Where the kind is kept: curated location chip + bag badge beside it. */
function Placement({ kind }) {
  const bag = kind.bag || kind.bin;
  if (!kind.location && !bag) {
    return <span style={{ font: '600 11px Outfit, sans-serif', color: 'var(--dim)', border: '1px dashed var(--borderStrong)', padding: '3px 8px', borderRadius: 5 }}>Not placed</span>;
  }
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {kind.location && (
        <span style={{ font: '600 11px Outfit, sans-serif', color: 'var(--text)', background: 'var(--surface2)', border: '1px solid var(--border)', padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap' }}>
          {kind.location}
        </span>
      )}
      {bag && (
        <span style={{ font: '600 11px Outfit, sans-serif', color: '#f59e0b', background: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.3)', padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap' }}>
          Bag {bag}
        </span>
      )}
    </div>
  );
}

function InlinePlacement({ kind, stockLocations, canManage, onSave }) {
  const [open, setOpen] = useState(false);
  const [locId, setLocId] = useState(kind.locationId ?? null);
  const [bagVal, setBagVal] = useState(kind.bag || kind.bin || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLocId(kind.locationId ?? null);
      setBagVal(kind.bag || kind.bin || '');
    }
  }, [open, kind.locationId, kind.bag, kind.bin]);

  const handleSave = async (e) => {
    e?.stopPropagation();
    setSaving(true);
    try {
      await onSave(kind.id, locId, bagVal);
      setOpen(false);
    } catch {
      // toast shown in onSave
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return <Placement kind={kind} />;
  }

  const content = (
    <div style={{ width: 250, padding: 4 }} onClick={(e) => e.stopPropagation()}>
      <div style={{ font: '700 12.5px Outfit, sans-serif', color: 'var(--text)', marginBottom: 8 }}>
        Set location & bag
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div>
          <div style={{ font: '600 11px Outfit, sans-serif', color: 'var(--muted)', marginBottom: 4 }}>Location</div>
          <Select
            allowClear
            size="small"
            placeholder="Select location"
            value={locId}
            onChange={(v) => setLocId(v ?? null)}
            style={{ width: '100%' }}
            dropdownStyle={{ zIndex: 1250 }}
            options={stockLocations.map((l) => ({ value: l.id, label: l.name }))}
          />
        </div>
        <div>
          <div style={{ font: '600 11px Outfit, sans-serif', color: 'var(--muted)', marginBottom: 4 }}>Bag</div>
          <Input
            size="small"
            placeholder="e.g. Bag 12"
            maxLength={32}
            value={bagVal}
            onChange={(e) => setBagVal(e.target.value)}
            onPressEnter={handleSave}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 4 }}>
          <button
            type="button"
            onClick={() => setOpen(false)}
            style={{
              height: 26, padding: '0 8px', borderRadius: 6, border: '1px solid var(--border)',
              background: 'var(--surface2)', color: 'var(--muted)', font: '600 11.5px Outfit, sans-serif', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            style={{
              height: 26, padding: '0 10px', borderRadius: 6, border: 'none',
              background: PRIMARY, color: '#fff', font: '700 11.5px Outfit, sans-serif', cursor: 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      content={content}
      placement="bottomLeft"
    >
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => e.stopPropagation()}
        title="Click to quickly assign location and bag"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          cursor: 'pointer',
          borderRadius: 6,
          padding: '2px 4px',
          margin: '-2px -4px',
        }}
        className="et-stock__ghost"
      >
        <Placement kind={kind} />
        <span style={{ fontSize: 10, color: 'var(--dim)', opacity: 0.8 }}>✎</span>
      </span>
    </Popover>
  );
}

function MovementRow({ movement, showKind }) {
  const meta = movementMeta(movement.type);
  const negative = movement.quantity < 0;
  const kind = movement.productKind;
  const detail = [
    movement.orderId ? `Order #${movement.orderId}` : null,
    movement.reason,
  ].filter(Boolean).join(' · ');

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) 70px minmax(0,1.2fr) 90px', borderBottom: '1px solid var(--border)' }}>
      <div style={{ padding: '11px 16px', minWidth: 0 }}>
        <span style={{ font: '600 10.5px monospace', color: meta.tint, background: meta.bg || `${meta.tint}24`, padding: '3px 7px', borderRadius: 5 }}>{meta.label}</span>
        {showKind && kind && (
          <div style={{ font: '600 12px Outfit, sans-serif', color: 'var(--text)', marginTop: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {[kind.productType, kind.productFormation, kind.fabricColor, kind.fabricPattern].filter(Boolean).join(' · ')}
          </div>
        )}
        {detail && <div style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)', marginTop: 5 }}>{detail}</div>}
      </div>
      <div style={{ padding: '11px 8px', textAlign: 'right', font: '700 13px monospace', color: negative ? STATUS.errorSoft : STATUS.ok }}>
        {negative ? movement.quantity : `+${movement.quantity}`}
      </div>
      <div style={{ padding: '11px 8px', font: '400 12.5px Outfit, sans-serif', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {movement.user || 'System'}
      </div>
      <div style={{ padding: '11px 16px 11px 8px', font: '400 12px monospace', color: 'var(--muted)' }}>
        {movement.createdAt ? dayjs(movement.createdAt).format('D MMM') : ''}
      </div>
    </div>
  );
}

function SummaryStat({ label, value, tint }) {
  return (
    <div>
      <div style={{ font: '600 10.5px Outfit, sans-serif', color: 'var(--dim)', letterSpacing: '.05em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ font: '700 19px monospace', color: tint, marginTop: 2 }}>{value}</div>
    </div>
  );
}

/**
 * A filter Popover's menu. Popovers portal to <body>, outside the element the
 * screen declares its CSS variables on, so the colours are plain hex — var(--x)
 * does not resolve out there.
 */
function MenuList({ items, active, onSelect, hint, darkMode }) {
  const t = THEME[darkMode ? 'dark' : 'light'];
  return (
    <div style={{ minWidth: 180, maxHeight: 320, overflowY: 'auto', background: t.surface2, borderRadius: 8 }}>
      <div style={{ padding: 6 }}>
        {items.map((it) => {
          const isActive = active !== undefined && active === it.key;
          return (
            <div
              key={String(it.key)}
              onClick={() => onSelect(it.key)}
              style={{ height: 34, borderRadius: 8, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', background: isActive ? t.hover : 'transparent' }}
            >
              {it.swatch && <ColorSwatch name={it.swatch} />}
              <span style={{ font: '600 12.5px Outfit, sans-serif', color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label}</span>
              {isActive && <CheckOutlined style={{ marginLeft: 'auto', color: PRIMARY, fontSize: 11 }} />}
            </div>
          );
        })}
      </div>
      {hint && (
        <div style={{ padding: '9px 12px', borderTop: `1px solid ${t.border}`, font: '400 11.5px Outfit, sans-serif', color: t.dim, lineHeight: 1.45 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function Chip({ label, value, onClear, tone }) {
  const isWarn = tone === 'warn';
  return (
    <span style={{
      height: 26, padding: '0 5px 0 9px', borderRadius: 7,
      background: isWarn ? 'rgba(251,191,36,.12)' : 'var(--surface2)',
      border: isWarn ? '1px solid rgba(251,191,36,.35)' : '1px solid var(--border)',
      color: isWarn ? STATUS.warn : 'var(--text)',
      font: '600 12px Outfit, sans-serif', display: 'flex', alignItems: 'center', gap: 7,
    }}>
      <span style={{ opacity: .75, fontWeight: 500, color: isWarn ? STATUS.warn : 'var(--muted)' }}>{label}</span>
      {value}
      <span onClick={onClear} style={{ width: 16, height: 16, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, cursor: 'pointer', background: isWarn ? 'rgba(251,191,36,.16)' : 'var(--surface)' }}>
        <CloseOutlined />
      </span>
    </span>
  );
}

function PageBtn({ children, onClick, active, disabled }) {
  return (
    <span
      onClick={disabled ? undefined : onClick}
      style={{
        height: 30, minWidth: 30, padding: '0 9px', borderRadius: 8,
        border: active ? 'none' : '1px solid var(--border)',
        background: active ? PRIMARY : 'var(--surface2)',
        color: active ? '#fff' : disabled ? 'var(--dim)' : 'var(--text)',
        font: active ? '700 12px monospace' : '600 12px monospace',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </span>
  );
}

function LoadingRow() {
  return (
    <div style={{ padding: '34px 18px', display: 'flex', justifyContent: 'center' }}>
      <Spin />
    </div>
  );
}

/**
 * Two empty states from the mock: nothing on hand (the in-stock filter is
 * hiding every kind) and an empty search.
 */
function EmptyState({ search, inStockOnly, hasFilters, onShowAll, onClear, onAddStock, canManage }) {
  const searching = !!search || hasFilters;
  return (
    <div style={{ padding: '32px 18px 36px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
      <span style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--surface2)', color: 'var(--dim)', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {searching ? <SearchOutlined /> : '▦'}
      </span>
      <div style={{ font: '600 13.5px Outfit, sans-serif' }}>
        {search
          ? <>No kinds match <span style={{ fontFamily: 'monospace', fontSize: 12.5, color: 'var(--activeText)' }}>{search}</span></>
          : hasFilters ? 'No kinds match these filters' : 'No stock on hand'}
      </div>
      <div style={{ font: '400 12.5px/1.5 Outfit, sans-serif', color: 'var(--muted)', maxWidth: 340 }}>
        {searching
          ? 'Search covers product type, fabric, color, pattern, formation and location. The in-stock filter is still applied.'
          : <>Turn off <span style={{ color: 'var(--text)' }}>In stock only</span> to view every registered kind, or click <span style={{ color: 'var(--text)' }}>+ Add stock</span> to record opening balance.</>}
      </div>
      <div style={{ display: 'flex', gap: 9, marginTop: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
        {searching && (
          <button type="button" onClick={onClear} style={{ height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', font: '600 12.5px Outfit, sans-serif', cursor: 'pointer' }}>
            Clear search
          </button>
        )}
        {inStockOnly && (
          <button type="button" onClick={onShowAll} style={{ height: 32, padding: '0 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', font: '600 12.5px Outfit, sans-serif', cursor: 'pointer' }}>
            Show all kinds
          </button>
        )}
        {canManage && (
          <button type="button" onClick={onAddStock} style={{ height: 32, padding: '0 14px', borderRadius: 8, border: 'none', background: PRIMARY, color: '#fff', font: '700 12.5px Outfit, sans-serif', cursor: 'pointer' }}>
            + Add stock
          </button>
        )}
      </div>
    </div>
  );
}

function KindColumnFilter({ productTypes, productTypeId, productFormations, productFormationId, onApply, darkMode }) {
  const [tab, setTab] = useState(productFormationId !== null && productTypeId === null ? 'formation' : 'type');
  const t = THEME[darkMode ? 'dark' : 'light'];
  return (
    <div style={{ minWidth: 200, background: t.surface2, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'flex', borderBottom: `1px solid ${t.border}`, background: t.surface }}>
        <button
          type="button"
          onClick={() => setTab('type')}
          style={{
            flex: 1, height: 32, border: 'none', background: tab === 'type' ? t.surface2 : 'transparent',
            color: tab === 'type' ? t.text : t.muted,
            font: '600 12px Outfit, sans-serif', cursor: 'pointer',
            borderBottom: tab === 'type' ? `2px solid ${PRIMARY}` : '2px solid transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          Type
          {productTypeId !== null && <span style={{ width: 6, height: 6, borderRadius: 3, background: PRIMARY }} />}
        </button>
        <button
          type="button"
          onClick={() => setTab('formation')}
          style={{
            flex: 1, height: 32, border: 'none', background: tab === 'formation' ? t.surface2 : 'transparent',
            color: tab === 'formation' ? t.text : t.muted,
            font: '600 12px Outfit, sans-serif', cursor: 'pointer',
            borderBottom: tab === 'formation' ? `2px solid ${PRIMARY}` : '2px solid transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          Formation
          {productFormationId !== null && <span style={{ width: 6, height: 6, borderRadius: 3, background: PRIMARY }} />}
        </button>
      </div>
      <div>
        {tab === 'type' ? (
          <MenuList
            items={[{ key: null, label: 'All types' }, ...productTypes.map((p) => ({ key: p.id, label: p.name }))]}
            active={productTypeId}
            onSelect={(v) => onApply({ productTypeId: v })}
            darkMode={darkMode}
          />
        ) : (
          <MenuList
            items={[{ key: null, label: 'All formations' }, ...productFormations.map((f) => ({ key: f.id, label: f.name }))]}
            active={productFormationId}
            onSelect={(v) => onApply({ productFormationId: v })}
            darkMode={darkMode}
          />
        )}
      </div>
    </div>
  );
}

function FabricColumnFilter({ fabricTypes, fabricTypeId, filterPatterns, fabricPatternId, onApply, darkMode }) {
  const [tab, setTab] = useState(fabricPatternId !== null && fabricTypeId === null ? 'pattern' : 'fabric');
  const t = THEME[darkMode ? 'dark' : 'light'];
  return (
    <div style={{ minWidth: 200, background: t.surface2, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'flex', borderBottom: `1px solid ${t.border}`, background: t.surface }}>
        <button
          type="button"
          onClick={() => setTab('fabric')}
          style={{
            flex: 1, height: 32, border: 'none', background: tab === 'fabric' ? t.surface2 : 'transparent',
            color: tab === 'fabric' ? t.text : t.muted,
            font: '600 12px Outfit, sans-serif', cursor: 'pointer',
            borderBottom: tab === 'fabric' ? `2px solid ${PRIMARY}` : '2px solid transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          Fabric
          {fabricTypeId !== null && <span style={{ width: 6, height: 6, borderRadius: 3, background: PRIMARY }} />}
        </button>
        <button
          type="button"
          onClick={() => setTab('pattern')}
          style={{
            flex: 1, height: 32, border: 'none', background: tab === 'pattern' ? t.surface2 : 'transparent',
            color: tab === 'pattern' ? t.text : t.muted,
            font: '600 12px Outfit, sans-serif', cursor: 'pointer',
            borderBottom: tab === 'pattern' ? `2px solid ${PRIMARY}` : '2px solid transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          Pattern
          {fabricPatternId !== null && <span style={{ width: 6, height: 6, borderRadius: 3, background: PRIMARY }} />}
        </button>
      </div>
      <div>
        {tab === 'fabric' ? (
          <MenuList
            items={[{ key: null, label: 'All fabrics' }, ...fabricTypes.map((f) => ({ key: f.id, label: f.name }))]}
            active={fabricTypeId}
            onSelect={(v) => onApply({ fabricTypeId: v })}
            darkMode={darkMode}
          />
        ) : (
          <MenuList
            items={[
              { key: null, label: 'All patterns' },
              { key: 'none', label: 'Solid (No pattern)' },
              ...filterPatterns.map((p) => ({ key: p.id, label: p.name })),
            ]}
            active={fabricPatternId}
            onSelect={(v) => onApply({ fabricPatternId: v })}
            hint={!fabricTypeId ? 'Pick a fabric to view its specific patterns.' : undefined}
            darkMode={darkMode}
          />
        )}
      </div>
    </div>
  );
}

function MobileStockFilters({
  productTypes, productTypeId,
  productFormations, productFormationId,
  fabricTypes, fabricTypeId,
  filterPatterns, fabricPatternId,
  fabrics, fabricColorId,
  stockLocations, locationId,
  onApply, onClear,
}) {
  const [draft, setDraft] = useState({
    productTypeId,
    productFormationId,
    fabricTypeId,
    fabricPatternId,
    fabricColorId,
    locationId,
  });

  const availablePatterns = useMemo(
    () => (draft.fabricTypeId ? filterPatterns : []),
    [draft.fabricTypeId, filterPatterns],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Field label="Product type">
        <Select
          allowClear
          placeholder="All types"
          value={draft.productTypeId ?? undefined}
          onChange={(v) => setDraft((d) => ({ ...d, productTypeId: v ?? null }))}
          options={productTypes.map((p) => ({ value: p.id, label: p.name }))}
        />
      </Field>
      <Field label="Formation">
        <Select
          allowClear
          placeholder="All formations"
          value={draft.productFormationId ?? undefined}
          onChange={(v) => setDraft((d) => ({ ...d, productFormationId: v ?? null }))}
          options={productFormations.map((f) => ({ value: f.id, label: f.name }))}
        />
      </Field>
      <Field label="Fabric">
        <Select
          allowClear
          placeholder="All fabrics"
          value={draft.fabricTypeId ?? undefined}
          onChange={(v) => setDraft((d) => ({ ...d, fabricTypeId: v ?? null, fabricPatternId: null }))}
          options={fabricTypes.map((f) => ({ value: f.id, label: f.name }))}
        />
      </Field>
      {draft.fabricTypeId && (
        <Field label="Pattern">
          <Select
            allowClear
            placeholder="All patterns"
            value={draft.fabricPatternId ?? undefined}
            onChange={(v) => setDraft((d) => ({ ...d, fabricPatternId: v ?? null }))}
            options={[
              { value: 'none', label: 'Solid (No pattern)' },
              ...availablePatterns.map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
        </Field>
      )}
      <Field label="Color">
        <Select
          allowClear
          placeholder="All colors"
          value={draft.fabricColorId ?? undefined}
          onChange={(v) => setDraft((d) => ({ ...d, fabricColorId: v ?? null }))}
          options={fabrics.map((c) => ({
            value: c.id,
            label: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <ColorSwatch name={c.name} />
                {c.name}
              </span>
            ),
          }))}
        />
      </Field>
      <Field label="Location">
        <Select
          allowClear
          placeholder="All locations"
          value={draft.locationId ?? undefined}
          onChange={(v) => setDraft((d) => ({ ...d, locationId: v ?? null }))}
          options={[
            ...stockLocations.map((l) => ({ value: l.id, label: l.name })),
            { value: 'none', label: 'Not placed' },
          ]}
        />
      </Field>
      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button type="button" onClick={onClear} style={{ ...sheetCancelBtn, flex: 1 }}>Clear all</button>
        <button type="button" onClick={() => onApply(draft)} style={{ ...sheetOkBtn, flex: 2 }}>Apply</button>
      </div>
    </div>
  );
}

export default ReadyStock;
